import React, { useEffect } from "react"
import { ReadAndLearnBreadcrumbs } from "../../components/read/shared/ReadAndLearnBreadcrumbs"
import { GroupComponentProps } from "./GroupParser"
import { Group } from "./utils"
import { LegalFooter } from "./components/LegalFooter"
import { ContentfulConditionalGate } from "#/lib/contentful/components/ContentfulConditionGate"
import { FindASessionButton } from "./components/FindSessionButton"
import {
  AppLockedDownModal,
  LockedDownModalType
} from "#/components/views/app/navigation/nav-modals/AppLockedDownModal"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { getHeap } from "./utils/heap"
import { Moderators } from "./components/Moderators"
import { ContentfulRawMarkdownRenderer } from "#/services/contentful"
import { Box, Stack, Tile, useTheme } from "@carrotfertility/carotene-core"
// eslint-disable-next-line no-restricted-imports
import { Container, FlexContainer, Divider } from "@carrotfertility/carotene" // please do not import from @carrotfertility/carotene after eslint rule added on 3/20/25
import { defaultMarkdownElements } from "#/services/markdown/elements"
import { Title } from "#/services/page-title/Title"
import { PageLayout } from "#/features/global-ui/page-layout"
import { PageBreadcrumbHeader } from "#/features/global-ui/page-header"
import { CarrotAcademySearch } from "#/components/read/CarrotAcademySearch"
import { useHistory, useLocation } from "react-router"
import { MemberActions } from "#/services/memberActionTracking/memberActions"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"
import { FavoriteButton } from "#/components/read/FavoriteButton"
import { FeedbackContainer } from "#/components/read/FeedbackContainer"
const Topics = ({ topicsMarkdown }: { topicsMarkdown: string }): JSX.Element => (
  <Tile sx={{ inlineSize: "100%", maxInlineSize: { xs: "100%", lg: "20rem" }, blockSize: "fit-content" }}>
    <ContentfulRawMarkdownRenderer>{topicsMarkdown}</ContentfulRawMarkdownRenderer>
  </Tile>
)

const GroupHeading = ({
  overview,
  title,
  groupId,
  categoryId
}: {
  overview: string
  title: string
  groupId: string
  categoryId: string
}): JSX.Element => {
  return (
    <Box display="flex" flexDirection="column" gap={(theme) => theme.spacing(theme.tokens.spacing.xs)}>
      <defaultMarkdownElements.h1.component {...defaultMarkdownElements.h1.props} variant="body2">
        {overview}
      </defaultMarkdownElements.h1.component>
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: theme.tokens.spacing.sm
        })}
      >
        <defaultMarkdownElements.h1.component {...defaultMarkdownElements.h1.props}>
          {title}
        </defaultMarkdownElements.h1.component>
        <Box>
          <FavoriteButton contentfulEntryId={groupId} categoryEntryId={categoryId} contentType="group" />
        </Box>
      </Box>
    </Box>
  )
}

const GroupDetails = ({
  group,
  groupSlugId,
  categoryId
}: {
  group: Group
  groupSlugId: string
  categoryId: string
}): JSX.Element => {
  const { overview, title, description, topics, calendlyUrl, reasonId, locationId, timeTapEnvironment } = group
  return (
    <FlexContainer direction={["column", null, null, "row"]} gap="giant">
      <FlexContainer width={[null, null, null, "70%"]} gap="large" direction="column">
        <FlexContainer gap="medium" direction="column">
          <GroupHeading overview={overview} title={title} groupId={group.id} categoryId={categoryId} />
          <ContentfulRawMarkdownRenderer>{description}</ContentfulRawMarkdownRenderer>
        </FlexContainer>
        <Container>
          <FindASessionButton
            sessionPickerUrl={calendlyUrl}
            groupSlugId={groupSlugId}
            reasonId={reasonId}
            locationId={locationId}
            timeTapEnvironment={timeTapEnvironment}
          />
        </Container>
      </FlexContainer>
      <Topics topicsMarkdown={topics} />
    </FlexContainer>
  )
}

const GroupLandingPage = ({ group, subcategory, category }: GroupComponentProps): JSX.Element => {
  const { isInAppLockdown } = useCurrentUser()

  const groupSlugId = `${group.slug}-${group.id}`
  useEffect(() => {
    if (isInAppLockdown) {
      getHeap().track("Carrot Plan Required Modal Rendered", {
        "Group Slug ID": groupSlugId
      })
    }
  }, [isInAppLockdown, groupSlugId])

  const theme = useTheme()
  const history = useHistory()
  const location = useLocation()
  const { mutate: addMemberAction } = useMemberActionTracking()

  useEffect(() => {
    addMemberAction({
      memberActionTaken: MemberActions.CARROT_ACADEMY_GROUP_VIEWED,
      metaData: {
        category: category?.fields?.navTitle,
        url: location.pathname,
        entryId: group.id,
        isCarrotPlanRequiredModalShown: isInAppLockdown
      }
    })
  }, [location.pathname, category, group.id, addMemberAction, isInAppLockdown])

  return (
    <>
      <Title title={group.title} productTitle="Carrot Academy" />
      <PageLayout
        header={
          <PageBreadcrumbHeader
            breadcrumbs={<ReadAndLearnBreadcrumbs category={category} subcategory={subcategory} />}
            search={<CarrotAcademySearch />}
          />
        }
      >
        <ContentfulConditionalGate conditions={group.conditions} allowConditionsOverride={true}>
          <Box
            gap={theme.spacing(theme.tokens.spacing.md)}
            data-group-id={groupSlugId}
            sx={{ marginBlockEnd: theme.spacing(theme.tokens.spacing.xxl) }}
          >
            <Stack
              spacing={theme.spacing(theme.tokens.spacing.xl)}
              marginTop={theme.spacing(theme.tokens.spacing.xxxl)}
            >
              <GroupDetails group={group} groupSlugId={groupSlugId} categoryId={category.sys.id} />
              <Moderators moderators={group?.moderators} />
              <FlexContainer direction="column" gap="large">
                <Divider color="lightGrey" />
                <FeedbackContainer contentfulEntryId={group.id} categoryEntryId={category.sys.id} contentType="group" />
              </FlexContainer>
              <LegalFooter />
            </Stack>
          </Box>
        </ContentfulConditionalGate>
        <AppLockedDownModal
          open={isInAppLockdown}
          onClose={() => history.goBack()}
          modalType={LockedDownModalType.Default}
          dismissible={false}
        />
      </PageLayout>
    </>
  )
}

export default GroupLandingPage
